import { onMounted, onUnmounted } from "vue";

type ScanHandler = (value: string) => void;

interface Options {
	/** Callback обработчик после считывания HID */
	onScan: ScanHandler;
	/** Интервал между нажатиями @default 40 */
	maxKeyInterval?: number;
	/** Минимельная длина кода @default 6 */
	minLength?: number;
	/** Добавлять прослушиватель на window автоматически @default true */
	registerListener?: boolean;
	/** Символ вместо служебной клавиши: сканер может слать, например, F8 вместо GS */
	mapKey?: (key: string) => string | null;
}

export function useHidScanner({ onScan, maxKeyInterval = 40, minLength = 6, registerListener = true, mapKey }: Options) {
	let buffer = "";
	let timestamps: number[] = [];
	let is_collecting = false;

	const reset = () => {
		buffer = "";
		timestamps = [];
		is_collecting = false;
	};

	const isFastEnough = () => {
		if (timestamps.length < 2) return false;

		const intervals = timestamps.slice(1).map((t, i) => t - timestamps[i]);

		const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
		return avg <= maxKeyInterval;
	};

	const onKeyDown = async (e: KeyboardEvent) => {
		const target = e.target as HTMLElement;
		if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || target.isContentEditable) return;
		const mappedKey = mapKey?.(e.key) ?? null;
		if (e.key.length > 1 && e.key !== "Enter" && mappedKey === null) return;

		const now = performance.now();

		if (!is_collecting) {
			is_collecting = true;
			buffer = "";
			timestamps = [];
		}

		timestamps.push(now);

		if (e.key === "Enter") {
			e.preventDefault();
			if (buffer.length >= minLength && isFastEnough()) {
				await onScan(buffer);
			}
			return reset();
		}

		if (mappedKey !== null) e.preventDefault();
		buffer += mappedKey ?? e.key;
	};

	onMounted(() => {
		if (registerListener) window.addEventListener("keydown", onKeyDown);
	});

	onUnmounted(() => {
		if (registerListener) window.removeEventListener("keydown", onKeyDown);
	});

	return { onKeyDown };
}
